import c from "classnames";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Responsive } from "react-grid-layout";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import { HistoricalActivityTimelineCard } from "@components/Cards/HistoricalActivityTimelineCard/HistoricalActivityTimelineCard";
import { ClientEmployeeAppUsageLineChart } from "@components/Charts";
import {
  EmployeeApplicationFilterForm,
  EmployeeApplicationFilterFormRef,
  EmployeeApplicationFilterFormValues,
} from "@components/Forms";
import {
  EmployeeAppCountUsageStat,
  EmployeeOwnedAppsCountStat,
  EmployeeUsageTimeStat,
} from "@components/Stats";
import { EmployeeApplicationsTableConfig } from "@components/Tables/EmployeeApplicationsTable/EmployeeApplicationsTableConfig";
import { EmployeeApplicationsTableColumnCustomization } from "@components/Tables/EmployeeApplicationsTable/UI/EmployeeApplicationsTableColumnCustomization";
import {
  DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_ORDER,
  DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/EmployeeApplicationsTable/UI/EmployeeApplicationsTableColumns";
import { EmployeeResourceGroupsTable } from "@components/Tables/EmployeeResourceGroupsTable/EmployeeResourceGroupsTable";
import { useEmployeeResourceGroupsTableExport } from "@components/Tables/EmployeeResourceGroupsTable/utils/useEmployeeResourceGroupsTableExport";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import classes from "./EmployeeDashboard.module.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const layout = [
  { i: "a", x: 0, y: 0, w: 4, h: 4 },
  { i: "b", x: 4, y: 0, w: 4, h: 4 },
  { i: "c", x: 8, y: 0, w: 4, h: 4 },
  { i: "timeline", x: 0, y: 4, w: 4, h: 44 },
  { i: "e", x: 4, y: 4, w: 8, h: 12 },
  { i: "f", x: 4, y: 16, w: 8, h: 32 },
];

const smLayout = [
  { i: "a", x: 0, y: 0, w: 12, h: 4 },
  { i: "b", x: 0, y: 4, w: 12, h: 4 },
  { i: "c", x: 0, y: 8, w: 12, h: 4 },
  { i: "timeline", x: 0, y: 12, w: 12, h: 20 },
  { i: "e", x: 0, y: 32, w: 12, h: 12 },
  { i: "f", x: 0, y: 44, w: 12, h: 32 },
];

const layouts = {
  lg: layout,
  md: layout,
  sm: smLayout,
  xs: smLayout,
  xxs: smLayout,
};

type Props = {
  clientEmployeeId: string;
  clientId: string;
};

export const EmployeeDashboard = ({ clientEmployeeId, clientId }: Props) => {
  const ref1 = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [dynamicLayouts, setDynamicLayouts] = useState(layouts);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- enable once we have a way to edit the layout
  const [isEditingLayout, _setIsEditingLayout] = useState(false);

  const [filterValues, setFilterValues] =
    useState<EmployeeApplicationFilterFormValues>({
      parentCategoryId: undefined,
      createdAt_Gte: undefined,
      createdAt_Lte: undefined,
    });

  const filterFormRef = useRef<EmployeeApplicationFilterFormRef>(null);

  const areFiltersActive = useMemo(() => {
    return Boolean(
      (filterValues.parentCategoryId &&
        filterValues.parentCategoryId.trim() !== "") ||
        filterValues.createdAt_Gte ||
        filterValues.createdAt_Lte,
    );
  }, [filterValues]);

  // Date range state - default to last 30 days
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => {
    const endDate = new Date();
    const startDate = dayjs().subtract(30, "days").toDate();
    return [startDate, endDate];
  });

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);

  // Initialize export functionality
  const { exportData, isExporting } = useEmployeeResourceGroupsTableExport({
    clientEmployeeId,
    parentCategoryId: filterValues.parentCategoryId,
    dateRange,
  });

  // Calculate days difference for the chart
  const daysToShow = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return 30;
    const start = dayjs(dateRange[0]);
    const end = dayjs(dateRange[1]);
    return Math.max(1, end.diff(start, "days") + 1);
  }, [dateRange]);

  const handleFilterClick = () => {
    setIsFilterPanelOpen(true);
  };

  const handleColumnSettingsClick = () => {
    setIsColumnSettingsOpen(true);
  };

  const handleExportClick = async () => {
    try {
      await exportData();
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleApplyFilters = useCallback(() => {
    // Get current values from the form and apply them
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setDateRange([
        currentFormValues.createdAt_Gte
          ? dayjs(currentFormValues.createdAt_Gte).toDate()
          : null,
        currentFormValues.createdAt_Lte
          ? dayjs(currentFormValues.createdAt_Lte).toDate()
          : null,
      ]);
    }
  }, []);

  const handleFilterChange = useCallback(
    (newValues: EmployeeApplicationFilterFormValues) => {
      setFilterValues(newValues);
      // Close the panel when form is submitted (e.g., via Enter key)
      setIsFilterPanelOpen(false);
      setDateRange([
        newValues.createdAt_Gte ? dayjs(newValues.createdAt_Gte).toDate() : null,
        newValues.createdAt_Lte ? dayjs(newValues.createdAt_Lte).toDate() : null,
      ]);
    },
    [],
  );

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    const endDate = new Date();
    const startDate = dayjs().subtract(30, "days").toDate();

    setFilterValues({
      parentCategoryId: undefined,
      createdAt_Gte: undefined,
      createdAt_Lte: undefined,
    });
    setDateRange([startDate, endDate]);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (ref1.current) {
        const offsetWidth = ref1.current.offsetWidth;
        setWidth(offsetWidth);

        // React Grid Layout default breakpoints: lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0
        // We only want to fit to screen (prevent scrolling) on larger devices where the layout is horizontal.
        if (offsetWidth >= 996) {
          const rect = ref1.current.getBoundingClientRect();
          // Calculate available height: window height minus top offset minus bottom padding (24px)
          const availableHeight = window.innerHeight - rect.top - 32;
          
          // Fixed rowHeight = 12, rowGap = 10
          // rowCount * 12 + (rowCount - 1) * 10 = availableHeight
          const availableRows = Math.floor((availableHeight + 10) / 22);

          // Top cards are h: 4. Ensure we have at least min rows for the bottom cards
          const remainingRows = Math.max(20, availableRows - 4);
          
          // timeline takes all remainingRows
          // e (chart) takes roughly 25% of remainingRows
          // f (table) takes roughly 75% of remainingRows
          const eHeight = Math.max(8, Math.floor(remainingRows * 0.25));
          const fHeight = remainingRows - eHeight;
          
          setDynamicLayouts({
            ...layouts,
            lg: [
              ...layout.slice(0, 3), // a, b, c
              { ...layout[3], h: remainingRows }, // timeline
              { ...layout[4], h: eHeight }, // e
              { ...layout[5], y: 4 + eHeight, h: fHeight }, // f
            ]
          });
        } else {
          setDynamicLayouts(layouts);
        }
      }
    };

    const observer = new ResizeObserver(handleResize);
    if (ref1.current) {
      observer.observe(ref1.current);
    }
    
    window.addEventListener("resize", handleResize);
    handleResize(); // initial set

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <TableColumnSettingsProvider
      tableId={EmployeeApplicationsTableConfig.tableId}
      defaultColumnSettings={DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_EMPLOYEE_APPLICATION_TABLE_COLUMN_ORDER}
    >
      <div className={classes.container} ref={ref1}>
        <Responsive
          layouts={dynamicLayouts}
          className={classes.responsive}
          rowHeight={12}
          width={width}
          cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
          isResizable={isEditingLayout}
          isDraggable={isEditingLayout}
          autoSize
          containerPadding={[0, 0]}
        >
          <div key="timeline" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <HistoricalActivityTimelineCard
                clientId={clientId}
                clientEmployeeId={clientEmployeeId}
                dateRange={dateRange}
              />
            </div>
          </div>
          <div key="a" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <EmployeeUsageTimeStat
                clientEmployeeId={clientEmployeeId}
                clientId={clientId}
              />
            </div>
          </div>
          <div key="b" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <EmployeeAppCountUsageStat
                clientEmployeeId={clientEmployeeId}
                clientId={clientId}
                dateRange={dateRange}
              />
            </div>
          </div>
          <div key="c" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <EmployeeOwnedAppsCountStat
                clientEmployeeId={clientEmployeeId}
                clientId={clientId}
              />
            </div>
          </div>
          <div key="e" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Card>
                <Card.Header>
                  <Card.Header.Title>
                    Resource group usage by user
                  </Card.Header.Title>
                </Card.Header>
                <Card.Body grow>
                  <div className={classes.chart}>
                    <ClientEmployeeAppUsageLineChart
                      employeeId={clientEmployeeId}
                      daysToShow={daysToShow}
                      startDate={dateRange[0] || undefined}
                      endDate={dateRange[1] || undefined}
                      parentCategoryId={filterValues.parentCategoryId}
                    />
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>
          <div key="f" className={classes.card}>
            <div className={c({ [classes.editing]: isEditingLayout })}>
              <Card grow stretch>
                <Card.Header>
                  <Card.Header.Title>Resource group usage</Card.Header.Title>
                  <Card.Header.Actions>
                    <Box direction="row" gap={8} align="center">
                      {areFiltersActive && (
                        <Text size={12} color={"textSecondary"}>
                          Filters are applied
                        </Text>
                      )}
                      <IconButton
                        size="sm"
                        icon="faFilter"
                        onClick={handleFilterClick}
                        outline={"sm"}
                        showBubble={areFiltersActive}
                        tooltip="Show filters"
                      />
                      <IconButton
                        size="sm"
                        icon="faTableColumns"
                        onClick={handleColumnSettingsClick}
                        outline={"sm"}
                        tooltip="Column settings"
                      />
                      <IconButton
                        size="sm"
                        icon={isExporting ? "faArrowsSpin" : "faFileCsv"}
                        onClick={handleExportClick}
                        outline={"sm"}
                        tooltip={isExporting ? "Exporting..." : "Export CSV"}
                        disabled={isExporting}
                      />
                    </Box>
                  </Card.Header.Actions>
                </Card.Header>
                <Card.Body grow>
                  <EmployeeResourceGroupsTable
                    clientId={clientId}
                    clientEmployeeId={clientEmployeeId}
                    pageSize={10}
                    showFooter={true}
                    showHeader={true}
                    showPageSizeDropdown={false}
                    dateRange={dateRange}
                    parentCategoryId={filterValues.parentCategoryId}
                  />
                </Card.Body>
              </Card>
            </div>
          </div>
        </Responsive>

        <SlideInPanel
          isOpen={isFilterPanelOpen}
          onClose={handleCloseFilterPanel}
          onCancel={handleFiltersReset}
          onApply={handleApplyFilters}
          cancelButtonText={areFiltersActive ? "Clear filters" : undefined}
          applyButtonText="Apply filters"
          title="Application Filters"
        >
          <EmployeeApplicationFilterForm
            ref={filterFormRef}
            onFilterChange={handleFilterChange}
            values={filterValues}
            errorsAPI={undefined}
          />
        </SlideInPanel>

        <EmployeeApplicationsTableColumnCustomization
          isOpen={isColumnSettingsOpen}
          onClose={() => setIsColumnSettingsOpen(false)}
        />
      </div>
    </TableColumnSettingsProvider>
  );
};
